import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslationService } from '../translations/services/translation.service';

export interface BreadcrumbItem {
  label?: string; // existing (kept for backward compatibility)
  labelKey?: string; // new: translation key
  link?: string;
  icon?: string;
}

export interface NavigationContext {
  source: string;
  breadcrumbLabel: string;
  additionalBreadcrumbs?: BreadcrumbItem[];
}

/**
 * Service to manage dynamic breadcrumb navigation context
 * especially when navigating from different sources like casing search
 */
@Injectable({
  providedIn: 'root'
})
export class BreadcrumbContextService {
  private translationService = inject(TranslationService);

  private navigationContextSubject = new BehaviorSubject<NavigationContext | null>(null);
  public navigationContext$ = this.navigationContextSubject.asObservable();

  /**
   * Set navigation context for breadcrumb management
   */
  setNavigationContext(context: NavigationContext): void {
    this.navigationContextSubject.next(context);
    // Also store in sessionStorage for persistence across page refreshes
    sessionStorage.setItem('navigationContext', JSON.stringify(context));
  }

  /**
   * Get current navigation context
   */
  getNavigationContext(): NavigationContext | null {
    // First try from behavior subject
    const current = this.navigationContextSubject.value;
    if (current) {
      return current;
    }

    // Fallback to sessionStorage
    try {
      const stored = sessionStorage.getItem('navigationContext');
      if (stored) {
        const context = JSON.parse(stored);
        this.navigationContextSubject.next(context);
        return context;
      }
    } catch (error) {
      console.warn('Failed to parse navigation context from sessionStorage:', error);
    }

    return null;
  }

  /**
   * Clear navigation context
   */
  clearNavigationContext(): void {
    this.navigationContextSubject.next(null);
    sessionStorage.removeItem('navigationContext');
  }

  /**
   * Clear navigation context if navigating from normal work order flow
   * This prevents casing search context from persisting when using regular navigation
   */
  clearContextForNormalNavigation(): void {
    const currentContext = this.getNavigationContext();
    if (currentContext && currentContext.source === 'casing-search') {
      this.clearNavigationContext();
    }
  }

  /**
   * Generate breadcrumb items based on current context
   */
  generateBreadcrumbs(
    currentPage: string,
    workOrderNumber?: string,
    casingId?: string,
    workOrderId?: string,
    fromWorkOrderMenu: boolean = false
  ): BreadcrumbItem[] {
    const context = this.getNavigationContext();
    const breadcrumbs: BreadcrumbItem[] = [{ link: '/home/dashboard', icon: 'home' }];

    if (context && context.source === 'casing-search') {
      // Navigation from casing search
      breadcrumbs.push(
        { labelKey: 'searchCasingsText', label: this.translationService.translate('searchCasingsText'), link: '/home/search-casings' },
        { labelKey: 'casingListText', label: this.translationService.translate('casingListText'), link: '/home/search-casings/results' }
      );

      // Add final page without IDs
      if (currentPage === 'work-order-details') {
        breadcrumbs.push({ labelKey: 'workOrderDetailsText', label: this.translationService.translate('workOrderDetailsText') });
      } else if (currentPage === 'work-order-line-details') {
        // For casing search flow, just add work order line details without work order details
        // This handles direct navigation from casing search to work order line details
        breadcrumbs.push({ labelKey: 'workOrderLineDetailsText', label: this.translationService.translate('workOrderLineDetailsText') });
      }
    } else {
      // Default work order navigation (from left menu)
      // Only add "Work Orders" breadcrumb if explicitly coming from work order menu
      if (fromWorkOrderMenu) {
        breadcrumbs.push({
          labelKey: 'workOrdersText',
          label: this.translationService.translate('workOrdersText'),
          link: '/home/work-orders'
        });
      }

      // Add current page specific breadcrumbs for normal flow
      if (currentPage === 'work-order-details') {
        breadcrumbs.push({ labelKey: 'workOrderDetailsText', label: this.translationService.translate('workOrderDetailsText') });
      } else if (currentPage === 'work-order-line-details') {
        // For normal flow, include the work order step
        // Always use workOrderId (PK) to ensure unique identification
        if (workOrderId) {
          breadcrumbs.push(
            {
              labelKey: 'workOrderDetailsText',
              label: this.translationService.translate('workOrderDetailsText'),
              link: `/home/work-orders/work-orders-details/${workOrderId}`
            },
            { labelKey: 'workOrderLineDetailsText', label: this.translationService.translate('workOrderLineDetailsText') }
          );
        } else {
          // Fallback: create breadcrumb without link if no ID available
          breadcrumbs.push(
            { labelKey: 'workOrderDetailsText', label: this.translationService.translate('workOrderDetailsText') },
            { labelKey: 'workOrderLineDetailsText', label: this.translationService.translate('workOrderLineDetailsText') }
          );
        }
      }
    }

    return breadcrumbs;
  }

  /**
   * Get the preferred active sidebar link based on navigation context
   */
  getPreferredActiveSidebarLink(): string | null {
    const context = this.getNavigationContext();

    if (context && context.source === 'casing-search') {
      // If coming from casing search, keep the search casings sidebar active
      return '/home/search-casings';
    }

    // Return null to use default URL-based matching
    return null;
  }

  /**
   * Handle explicit sidebar navigation - clears context when user intentionally switches modules
   * This should be called when user clicks on sidebar items, not on programmatic navigation
   */
  handleSidebarNavigation(targetPath: string): void {
    const context = this.getNavigationContext();

    // If there's a casing search context and user explicitly navigates to ANY other module, clear it
    if (context && context.source === 'casing-search' && targetPath !== '/home/search-casings') {
      this.clearNavigationContext();
    }
  }

  /**
   * Mark that navigation is happening programmatically from casing search
   * This prevents the context from being cleared during legitimate casing search flow
   */
  private isProgrammaticNavigation = false;

  /**
   * Set flag before programmatic navigation from casing search
   */
  setProgrammaticNavigation(value: boolean): void {
    this.isProgrammaticNavigation = value;
  }

  /**
   * Check if current navigation is programmatic (from casing search services)
   */
  isProgrammaticNavigationActive(): boolean {
    return this.isProgrammaticNavigation;
  }
}
